import Game from 'Game';
import { IGameState } from 'State/Game/IGameState';
import GameObject, { GameObjectEvent } from 'GameObject/GameObject';
import { applyDefaultButtonAnimation } from 'System/UI/UIHelper';
import Interactable from 'Component/Interactable';
import { FarmButtonLayout } from 'Layouts/Farm/FarmButtonLayout';
import SpriteComponent from 'Component/Render/Sprite';
import TextComponent from 'Component/Render/Text';
import TransformComponent from 'Component/Transform';
import Scene from 'Scene/Scene';
import RelativePosition from 'Component/Render/RelativePosition';
import RenderSystem, { RenderSystemEvent } from 'System/Render/RenderSystem';
import PIXIRenderer from 'System/Render/PIXIRenderer';
import { FARM_UNLOCK_LEVEL } from 'Constants/Farm';
import Renderable from 'Component/Renderable';
import { GameActions } from 'State/Game/GameActions';
import CleanupManager from 'Utility/CleanupManager';
import { FarmStateHelper } from 'State/Player/Farm/FarmStateHelper';
import { StartAnimationClip } from 'System/Animation/AnimationHelper';
import { AnimationClipEvent } from 'System/Animation/AnimationClip';
import Point from 'Utility/Point';
import StateComponent from 'Component/State';
import { PulseLoop, UIPopIn } from 'System/Animation/Clip';
import { farmTransitionSceneId, GameScene } from 'State/Game/GameScene';
import { TimerType } from 'System/Timer/TimerTypes';
import { PetsStateHelper } from 'State/Player/Pets/PetsStateHelper';
import { createSelector, OutputSelector } from 'reselect';
import TimerStateHelper from 'State/Timer/TimerStateHelper';
import DateHelper from 'Utility/DateHelper';
import { FARM_MINI_GAME_TIMER_DURATION } from 'System/Timer/Timers/FarmMiniGameTimer';
import getVisibleItems = FarmStateHelper.getVisibleItems;
import areAnyItemsUnseen = FarmStateHelper.areAnyItemsUnseen;
import isAnyPetUnlocked = PetsStateHelper.isAnyPetUnlocked;

export const FARM_BUTTON_NAME = 'farmButton';

export default class FarmButton extends GameObject<PIXI.Container> {
    public readonly layout: any;
    private readonly cleanupManager: CleanupManager = new CleanupManager();
    private notificationSelector: OutputSelector<
        [(state: IGameState) => void],
        any,
        (res1: boolean, res2: boolean) => any
    >;
    private farmLockedSelector: OutputSelector<[(state: IGameState) => void], void, (res: boolean) => void>;

    constructor() {
        super();
        this.layout = new FarmButtonLayout();
    }

    public onCreate(props: { scene: Scene }): void {
        this.name = FARM_BUTTON_NAME;
        this.addChild(this.root);
        this.layout.root.addComponent(RelativePosition, { x: 0.545, y: 0.915 });

        // Setup button
        applyDefaultButtonAnimation(this.layout.myFarmButton);
        this.layout.myFarmButton.addComponent(Interactable, { toggleable: true });
        this.layout.myFarmButton.on(GameObjectEvent.SELECTED, this.handleOnClick);
        RenderSystem.on(RenderSystemEvent.RESIZE, this.handleResize);

        // Handle listeners
        props.scene.on('EnableMyFarmButton', this.enableFarmButton);
        this.cleanupManager.add(() => {
            props.scene.off('EnableMyFarmButton', this.enableFarmButton);
        });

        this.handleResize();

        this.addComponent(StateComponent, {
            handleStateChanged: this.handleStateChanged.bind(this),
        });

        this.layout.notification.getComponent(Renderable).visible = false;

        const isFarmUnlocked = (state: IGameState) => FarmStateHelper.isFarmFeatureUnlocked(state);

        this.farmLockedSelector = createSelector(isFarmUnlocked, farmUnlocked => {
            if (this.layout.root.destroyed) return;

            this.layout.myFarmButton.getComponent(SpriteComponent).texture = farmUnlocked
                ? 'my_farm_icon'
                : 'my_farm_icon_grey';
            this.layout.myFarmButton
                .getComponent(Interactable)
                .update({ enabled: farmUnlocked, buttonMode: farmUnlocked });
            this.layout.titleText.getComponent(TextComponent).text = Game.locale
                .getText('Level')
                .replace('[x]', FARM_UNLOCK_LEVEL.toString());
            this.layout.titleText.getComponent(Renderable).visible = !farmUnlocked;
            this.layout.lock.getComponent(SpriteComponent).visible = !farmUnlocked;
        });

        this.notificationSelector = createSelector(
            isFarmUnlocked,
            (state: IGameState) => {
                const hasUnseenItems = areAnyItemsUnseen(
                    getVisibleItems(state, false, true).map(item => item.itemId),
                    state.playerState.decoItemsSeen
                );

                const isPetMiniGameAvailable =
                    isAnyPetUnlocked(state.playerState.petsState) &&
                    TimerStateHelper.isTimerComplete(state, TimerType.FARM_MINI_GAME, FARM_MINI_GAME_TIMER_DURATION);

                return hasUnseenItems || isPetMiniGameAvailable;
            },
            (farmUnlocked, showNotification) => {
                if (this.layout.root.destroyed) return;

                const notificationRenderable = this.layout.notification.getComponent(Renderable);

                if (farmUnlocked && showNotification) {
                    notificationRenderable.visible = true;
                    StartAnimationClip(
                        new UIPopIn(this.layout.notification).once(AnimationClipEvent.COMPLETE, () => {
                            StartAnimationClip(
                                new PulseLoop(this.layout.notification, {
                                    duration: 350,
                                    scale1: new Point(0.95, 0.95),
                                    scale2: new Point(1.1, 1.1),
                                    interval: 2,
                                    delay: 2000,
                                })
                            );
                        })
                    );
                } else {
                    notificationRenderable.visible = false;
                }
            }
        );
    }

    public get root(): GameObject {
        return this.layout.root;
    }

    public setInteractive(value: boolean): void {
        this.layout.myFarmButton.getComponent(Interactable).enabled = value;

        if (value) {
            this.layout.myFarmButton.getComponent(Interactable).toggle(true);
        }
    }

    private readonly enableFarmButton = (): void => {
        if (this.layout.root.destroyed) return;
        this.layout.myFarmButton.getComponent(SpriteComponent).texture = 'my_farm_icon';
        this.layout.myFarmButton.getComponent(Interactable).update({ enabled: true, buttonMode: true });
    };

    private handleStateChanged(state: IGameState): void {
        if (this.layout.root.destroyed) return;
        this.farmLockedSelector(state);
        this.notificationSelector(state);
    }

    private readonly handleOnClick = (): void => {
        if (Game.store.getState().activeScene.result === GameScene.WORLD) {
            GameActions.loadFarmScene({ loadingSceneId: farmTransitionSceneId });
        }
    };

    private readonly handleResize = () => {
        this.layout.root.getComponent(TransformComponent).y = PIXIRenderer.getOffsetY() * 0.9;
    };

    public destroy(): void {
        super.destroy();
        this.cleanupManager.cleanUpAll();
    }
}
